/**
 * Utilities for formatting and working with relative dates
 */

/**
 * Preset configuration for relative dates
 */
export interface RollingDatePreset {
  id: string;
  label: string;
  value: number;
  unit: 'hour' | 'day' | 'week' | 'month' | 'year';
  direction: 'past' | 'future';
}

/**
 * Predefined relative date presets
 * Includes both past and future presets with proper labels
 */
export const ROLLING_DATE_PRESETS: RollingDatePreset[] = [
  // Special case: Today (always shows regardless of direction)
  { id: 'today', label: 'Today', value: 0, unit: 'day', direction: 'past' },

  // Past presets
  { id: '24h_ago', label: '24 hours ago', value: 24, unit: 'hour', direction: 'past' },
  { id: '1w_ago', label: '1 week ago', value: 1, unit: 'week', direction: 'past' },
  { id: '1m_ago', label: '1 month ago', value: 1, unit: 'month', direction: 'past' },
  { id: '1y_ago', label: '1 year ago', value: 1, unit: 'year', direction: 'past' },

  // Future presets
  { id: '24h_from_now', label: '24 hours from now', value: 24, unit: 'hour', direction: 'future' },
  { id: '1w_from_now', label: '1 week from now', value: 1, unit: 'week', direction: 'future' },
  { id: '1m_from_now', label: '1 month from now', value: 1, unit: 'month', direction: 'future' },
  { id: '1y_from_now', label: '1 year from now', value: 1, unit: 'year', direction: 'future' },
];

/**
 * Get preset ID based on current relative values
 * Special handling: 0 days/weeks/months/years always matches "today"
 * @param value - The numeric value
 * @param unit - The time unit
 * @param direction - Direction (past/future)
 * @returns Preset ID if matches, null otherwise
 */
export function getMatchingPresetId(value: number, unit: 'minute' | 'hour' | 'day' | 'week' | 'month' | 'year', direction: 'past' | 'future'): string | null {
  // Special case: 0 values for day/week/month/year units always match "today"
  if (value === 0 && ['day', 'week', 'month', 'year'].includes(unit)) {
    return 'today';
  }

  const preset = ROLLING_DATE_PRESETS.find(
    p => p.value === value && p.unit === unit && p.direction === direction
  );

  return preset ? preset.id : null;
}

/**
 * Get preset by ID
 * @param presetId - The preset ID
 * @returns RollingDatePreset or null if not found
 */
export function getPresetById(presetId: string): RollingDatePreset | null {
  return ROLLING_DATE_PRESETS.find(p => p.id === presetId) || null;
}

/**
 * Format semantic relative date as human-readable text
 * @param value - The numeric value (1, 5, 30)
 * @param unit - The time unit
 * @param direction - Direction (past/future)
 * @returns Formatted string like "1 week ago" or "5 hours from now"
 */
export function formatSemanticRelativeDate(value: number, unit: 'minute' | 'hour' | 'day' | 'week' | 'month' | 'year', direction: 'past' | 'future'): string {

  const unitText = getUnitDisplayName(unit, value);

  if (direction === 'future') {
    return `${value} ${unitText} from now`;
  } else {
    return `${value} ${unitText} ago`;
  }
}

/**
 * Get display name for time unit (singular/plural)
 * @param unit - The time unit
 * @param value - The numeric value (for plural/singular)
 * @returns Display name like "minute", "minutes", "hour", "hours", etc.
 */
export function getUnitDisplayName(unit: 'minute' | 'hour' | 'day' | 'week' | 'month' | 'year', value: number): string {
  const unitMap = {
    minute: value === 1 ? 'minute' : 'minutes',
    hour: value === 1 ? 'hour' : 'hours',
    day: value === 1 ? 'day' : 'days',
    week: value === 1 ? 'week' : 'weeks',
    month: value === 1 ? 'month' : 'months',
    year: value === 1 ? 'year' : 'years',
  };

  return unitMap[unit];
}

/**
 * Generate relative time syntax string with semantic units
 * @param value - The numeric value (1, 5, 30)
 * @param unit - The time unit
 * @param direction - Direction (past/future)
 * @returns String like "+1w", "-5h", "+30m"
 */
export function generateRelativeTimeString(value: number, unit: 'minute' | 'hour' | 'day' | 'week' | 'month' | 'year', direction: 'past' | 'future'): string {
  const sign = direction === 'future' ? '+' : '-';
  const unitChar = getUnitCharacter(unit);
  return `${sign}${value}${unitChar}`;
}

/**
 * Get character representation of time unit
 * @param unit - The time unit
 * @returns Character like 'm', 'h', 'd', 'w', 'M'
 */
export function getUnitCharacter(unit: 'minute' | 'hour' | 'day' | 'week' | 'month' | 'year'): string {
  const unitMap = {
    minute: 'm',
    hour: 'h',
    day: 'd',
    week: 'w',
    month: 'M',
    year: 'Y',
  };

  return unitMap[unit];
}

/**
 * Parse relative time string back to semantic components
 * @param relativeString - String like "+1w", "-5h", "+30m"
 * @returns Object with value, unit, and direction, or null if invalid
 */
export function parseRelativeTimeString(relativeString: string): { value: number; unit: 'minute' | 'hour' | 'day' | 'week' | 'month' | 'year'; direction: 'past' | 'future' } | null {
  const match = /^([+-])(\d+)([mhdwMY])$/.exec(relativeString);
  if (!match) return null;

  const sign = match[1];
  const value = parseInt(match[2], 10);
  const unitChar = match[3];

  const unitMap: Record<string, 'minute' | 'hour' | 'day' | 'week' | 'month' | 'year'> = {
    m: 'minute',
    h: 'hour',
    d: 'day',
    w: 'week',
    M: 'month',
    Y: 'year',
  };

  const unit = unitMap[unitChar];
  if (!unit) return null;

  return {
    value,
    unit,
    direction: sign === '+' ? 'future' : 'past',
  };
}

/**
 * Convert semantic time unit and value to milliseconds
 * @param value - The numeric value (e.g., 5, 30, 1)
 * @param unit - The time unit (minute, hour, day, week, month, year)
 * @returns Milliseconds offset
 */
export function convertSemanticUnitToMs(value: number, unit: 'minute' | 'hour' | 'day' | 'week' | 'month' | 'year'): number {
  let offsetMs = 0;

  switch (unit) {
    case 'minute': offsetMs = value * 60 * 1000; break;
    case 'hour': offsetMs = value * 60 * 60 * 1000; break;
    case 'day': offsetMs = value * 24 * 60 * 60 * 1000; break;
    case 'week': offsetMs = value * 7 * 24 * 60 * 60 * 1000; break;
    case 'month': offsetMs = value * 30 * 24 * 60 * 60 * 1000; break;
    case 'year': offsetMs = value * 365 * 24 * 60 * 60 * 1000; break;
  }
  return offsetMs;
}

/**
 * Convert relative date filter to absolute Date
 *
 * Handles different time units appropriately:
 * - For minutes/hours: Uses exact time from current moment
 * - For days/weeks/months/years: Uses date only (sets to start/end of day)
 *
 * @param value - The numeric value (e.g., 5, 30, 1)
 * @param unit - The time unit
 * @param direction - Direction ('past' = ago, 'future' = from now)
 * @param isEndOfRange - If true, sets time to end of day (23:59:59.999), else start of day (00:00:00.000)
 * @returns Calculated absolute Date
 *
 */
export function convertRelativeToAbsoluteDate(
  value: number,
  unit: 'minute' | 'hour' | 'day' | 'week' | 'month' | 'year',
  direction: 'past' | 'future',
  isEndOfRange: boolean = false
): Date {
  const now = new Date();
  const ms = convertSemanticUnitToMs(value, unit);

  // Calculate the target date
  const targetDate = new Date(now);
  if (direction === 'past') {
    targetDate.setTime(now.getTime() - ms);
  } else {
    targetDate.setTime(now.getTime() + ms);
  }

  // For days/weeks/months/years, adjust to start or end of day
  // For minutes/hours, keep the exact time
  const isDayBasedUnit = ['day', 'week', 'month', 'year'].includes(unit);

  if (isDayBasedUnit) {
    if (isEndOfRange) {
      // End of day: 23:59:59.999
      targetDate.setHours(23, 59, 59, 999);
    } else {
      // Start of day: 00:00:00.000
      targetDate.setHours(0, 0, 0, 0);
    }
  }

  return targetDate;
}
